class Manuals extends HTMLElement {
  constructor() {
    super();
    // Storefront API access token is public by design (read-only, scoped).
    // Allow the section to override endpoint/token via data attributes.
    this.endpoint =
      this.dataset.endpoint ||
      'https://westinghousehome.myshopify.com/api/2025-10/graphql.json';
    this.token = this.dataset.token || 'ae677dd0db80d9d9b438e4ae75fd40b6';

    this.manualTemplate = this.querySelector('[data-manual-template]');
    this.manualContainer = this.querySelector('[data-manuals]');
    this.documentSelect = this.querySelector('[data-document-type]');
    this.categorySelect = this.querySelector('[data-category-filter]');
    this.modelsSelect = this.querySelector('[data-models-filter]');
    this.input = this.querySelector('[data-input]');
    this.reset = this.querySelector('[data-reset]');
    this.showMore = this.querySelector('[data-show-more]');
    this.showMoreContainer = this.querySelector('[data-show-more-container]');

    this.allMetaobjects = [];
    this.filteredManuals = [];
    this.pageSize = 50;
    this.visibleCount = this.pageSize;

    // Dropdown facets. `getValues` returns an array so single fields and list
    // fields (models) share the same filtering + option-building logic.
    this.facets = [
      {
        select: this.categorySelect,
        getValues: (m) => [this.getField(m, 'category')],
      },
      {
        select: this.documentSelect,
        getValues: (m) => [this.getField(m, 'document_type')],
      },
      {
        select: this.modelsSelect,
        getValues: (m) => this.getModelsArray(m),
      },
    ];
  }

  connectedCallback() {
    if (this.documentSelect) {
      this.documentSelect.addEventListener('change', () => this.applyFilters());
    }
    if (this.categorySelect) {
      this.categorySelect.addEventListener('change', () => this.applyFilters());
    }
    if (this.modelsSelect) {
      this.modelsSelect.addEventListener('change', () => this.applyFilters());
    }
    if (this.input) {
      this.input.addEventListener(
        'input',
        this.debounce(() => this.applyFilters(), 500)
      );
    }
    if (this.reset) {
      this.reset.addEventListener('click', () => this.resetFilters());
    }
    if (this.showMore) {
      this.showMore.addEventListener('click', () => {
        this.visibleCount += this.pageSize;
        this.renderVisible();
      });
    }

    this.fetchAllMetaobjects();
  }

  // ---------------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------------

  async fetchAllMetaobjects() {
    this.renderMessage('Loading manuals…');

    const query = `
      query GetManuals($cursor: String) {
        metaobjects(first: 250, type: "manuals", after: $cursor) {
          pageInfo { hasNextPage endCursor }
          nodes {
            id
            fields {
              key
              value
              reference {
                __typename
                ... on MediaImage { image { url altText width height } }
                ... on GenericFile { url mimeType }
              }
            }
          }
        }
      }
    `;

    const allMetaobjects = [];
    let hasNextPage = true;
    let cursor = null;

    try {
      while (hasNextPage) {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.token,
          },
          body: JSON.stringify({ query, variables: { cursor } }),
        });

        const result = await response.json();
        const data = result?.data?.metaobjects;

        if (!data) {
          throw new Error(JSON.stringify(result.errors || result));
        }

        allMetaobjects.push(...data.nodes);
        hasNextPage = data.pageInfo.hasNextPage;
        cursor = data.pageInfo.endCursor;
      }
    } catch (error) {
      console.error('Failed to load manuals:', error);
      // Show whatever loaded before the failure; otherwise an error message.
      if (allMetaobjects.length === 0) {
        this.renderMessage('Unable to load manuals. Please try again later.');
        return;
      }
    }

    this.allMetaobjects = allMetaobjects;
    this.applyFilters();
  }

  // ---------------------------------------------------------------------------
  // Filters (single source of truth)
  // ---------------------------------------------------------------------------

  // Always starts from the full dataset and applies every active filter
  // together — category + document type + models + search — so they compose.
  applyFilters() {
    const manuals = this.getFilteredManuals();

    // New filter result: reset pagination back to the first page.
    this.filteredManuals = manuals;
    this.visibleCount = this.pageSize;
    this.renderVisible();

    // Cascade: rebuild each dropdown to only offer values that still have
    // results given the other active filters.
    this.refreshFilterOptions();
  }

  // Returns the manuals matching the active filters. `exclude` skips one facet
  // (used when building that facet's own options so it stays switchable).
  getFilteredManuals(exclude = null) {
    let manuals = this.allMetaobjects || [];

    for (const facet of this.facets) {
      if (facet === exclude || !facet.select) continue;
      const value = facet.select.value;
      if (!value || value === 'All') continue;
      manuals = manuals.filter((m) => facet.getValues(m).includes(value));
    }

    const searchTerm = this.input ? this.input.value.trim() : '';
    if (searchTerm) {
      manuals = this.getSearchResults(manuals, searchTerm);
    }

    return manuals;
  }

  // Rebuilds each dropdown's options from the values still reachable given the
  // OTHER filters, annotated with a remaining-results count, e.g. "User Manuals (12)".
  refreshFilterOptions() {
    for (const facet of this.facets) {
      if (!facet.select) continue;

      const base = this.getFilteredManuals(facet);
      const counts = new Map();
      for (const manual of base) {
        for (const raw of facet.getValues(manual)) {
          const value = (raw || '').trim();
          if (!value) continue;
          counts.set(value, (counts.get(value) || 0) + 1);
        }
      }
      this.rebuildSelectOptions(facet.select, counts);
    }
  }

  // Replaces the dynamic <option>s (keeping the placeholder + "All") and
  // preserves the current selection even if it now has zero results.
  rebuildSelectOptions(select, counts) {
    const current = select.value;

    Array.from(select.options).forEach((opt) => {
      if (opt.value !== '' && opt.value !== 'All') opt.remove();
    });

    const fragment = document.createDocumentFragment();
    Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([value, count]) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        fragment.appendChild(option);
      });
    select.appendChild(fragment);

    // Keep the current selection visible/selectable even if now zero-count.
    if (current && current !== 'All' && !counts.has(current)) {
      const option = document.createElement('option');
      option.value = current;
      option.textContent = current;
      select.appendChild(option);
    }
    select.value = current;
  }

  // Renders only the currently-visible slice and toggles the "Show More" button.
  renderVisible() {
    this.renderManuals(this.filteredManuals.slice(0, this.visibleCount));

    if (!this.showMoreContainer) return;
    const remaining = this.filteredManuals.length - this.visibleCount;
    if (remaining > 0) {
      if (this.showMore) this.showMore.textContent = `Show More (${remaining})`;
      // Inline style beats the element's `flex` utility class (a `hidden`
      // attribute would be overridden by `.flex { display: flex }`).
      this.showMoreContainer.style.display = 'flex';
    } else {
      this.showMoreContainer.style.display = 'none';
    }
  }

  resetFilters() {
    if (this.input) this.input.value = '';
    if (this.documentSelect) this.documentSelect.value = '';
    if (this.categorySelect) this.categorySelect.value = '';
    if (this.modelsSelect) this.modelsSelect.value = '';
    this.applyFilters();
  }

  // Scores/sorts a given list against the search term and returns the matches.
  // Pure (no rendering) so it composes with the dropdown filters above.
  getSearchResults(manuals, searchTerm) {
    const needle = this.stringNormalize(searchTerm);

    return manuals
      .map((manual) => {
        const match = (key, weight) =>
          this.stringNormalize(this.getField(manual, key)).includes(needle)
            ? weight
            : 0;

        const score =
          match('title', 100) +
          match('models', 30) +
          match('status', 20) +
          match('document_type', 10) +
          match('category', 10);

        return { manual, score };
      })
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ manual }) => manual);
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  renderManuals(manuals) {
    if (!this.manualContainer || !this.manualTemplate) return;

    if (!manuals.length) {
      this.renderMessage('No manuals match your search.');
      return;
    }

    const fragment = document.createDocumentFragment();

    manuals.forEach((manual) => {
      const clone = this.manualTemplate.content.cloneNode(true);
      if (!(clone instanceof DocumentFragment)) return;

      try {
        const { a, title, model, type, status, li } =
          this.getTemplateElements(clone);

        a.setAttribute('title', this.getField(manual, 'title') || 'Manual');
        a.setAttribute('href', this.getFileUrl(manual));
        a.setAttribute('target', '_blank');

        title.textContent = this.getField(manual, 'title');
        model.textContent = this.getModels(manual);
        type.textContent = this.getField(manual, 'document_type') || '—';
        status.textContent = this.getField(manual, 'status') || '—';

        li.dataset.type = this.getField(manual, 'document_type') || '';
        li.dataset.category = this.getField(manual, 'category') || '';

        fragment.appendChild(li);
      } catch (error) {
        console.error('Error rendering manual item.', error);
      }
    });

    this.manualContainer.replaceChildren(fragment);
  }

  renderMessage(text) {
    if (!this.manualContainer) return;
    const li = document.createElement('li');
    li.className = 'w-full p-2 text-start font-heading-light';
    li.textContent = text;
    this.manualContainer.replaceChildren(li);
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  getField(manual, key) {
    return manual.fields.find((f) => f.key === key)?.value || '';
  }

  // Resolves the link: attached file reference first, then a plain URL field.
  getFileUrl(manual) {
    const reference = manual.fields.find((f) => f.key === 'file_url')?.reference;
    if (reference?.__typename === 'GenericFile') {
      return reference.url || '#';
    }
    if (reference?.__typename === 'MediaImage') {
      return reference.image?.url || '#';
    }
    return this.getField(manual, 'manual_url') || '#';
  }

  // `models` is a list field stored as a JSON array string. Returns the parsed
  // array (empty if none), tolerating malformed/non-array values.
  getModelsArray(manual) {
    const raw = this.getField(manual, 'models');
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [raw];
    } catch {
      return [raw];
    }
  }

  // Display string for the Models column; falls back to "ALL" when none listed.
  getModels(manual) {
    const models = this.getModelsArray(manual);
    return models.length ? models.join(', ') : 'ALL';
  }

  getTemplateElements(fromElement) {
    const selectors = {
      title: '[data-title]',
      model: '[data-model]',
      type: '[data-type]',
      status: '[data-status]',
      a: 'a',
      li: 'li',
    };

    return Object.entries(selectors).reduce((acc, [key, selector]) => {
      const el = fromElement.querySelector(selector);
      if (!(el instanceof HTMLElement)) {
        throw new Error(`Error parsing template: ${selector}`);
      }
      acc[key] = el;
      return acc;
    }, {});
  }

  stringNormalize(str) {
    return str
      .toLowerCase()
      .normalize('NFD') // Decompose accents
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .trim();
  }

  debounce(fn, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

customElements.define('manual-items', Manuals);
